
import axios from 'axios';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  onMounted,
  computed,
} from 'vue';

import PrimeVueMessage from 'primevue/message';

import type {
  EntriesPaginator,
  Payouts,
} from '@/types';

import {
  ENTRIES,
  PAYOUT_TYPES_MAP,
  PAYOUT_PERIOD_WEEKLY,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntries from '@/composable/useEntries';

import useUrlState from '@/composable/useUrlState';

export default defineComponent({
  components: {
    PrimeVueMessage,
    User: defineAsyncComponent(() => import('@/components/User.vue')),
    PayoutMethodColumn: defineAsyncComponent(() => import('@/components/PayoutMethodColumn.vue')),
  },
  props: {
    title_translate_key: {
      type: String,
      default: 'payouts',
    },
    type: {
      type: Number,
      default: null,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    showTerms: {
      type: Boolean,
      default: true,
    },
    showProjectColumn: {
      type: Boolean,
      default: true,
    },
    showUserColumn: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const { activeUser: user } = useUser();

    const {
      entriesLoading: projectsLoading,
      entries: projects,
      fetchEntries: fetchProjects,
    } = useEntries();

    const payouts = ref<Payouts>(ENTRIES);

    const payoutsFilters = useUrlState('filters', {
      project_id: null,
      user_id: null,
    });

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? payoutsFilters.project_id : undefined,
    }));

    const fetchPayouts = async (payoutsPaginator: EntriesPaginator): Promise<void> => {
      const response = await axios.post(
        '/api/payout/getPayoutsPaginator',
        {
          ...payoutsPaginator,
          ...payoutsFilters,
          type: props.type,
          ...props.filters,
        },
      );

      payouts.value = response.data as Payouts;
    };

    const onProjectChange = (): void => {
      payoutsFilters.user_id = null;
    };

    const payoutInfo = computed((): string => {
      let info = '';

      if (user.payout_period === PAYOUT_PERIOD_WEEKLY) {
        info = 'payout_terms_weekly';
      } else {
        info = 'payout_terms';
      }

      return info;
    });

    onMounted(async (): Promise<void> => {
      if (user.isSuperAdmin()) {
        await fetchProjects('/api/project/getProjects');
      }
    });

    return {
      PAYOUT_TYPES_MAP,
      user,
      projectsLoading,
      projects,
      payouts,
      payoutsFilters,
      usersParams,
      payoutInfo,
      fetchPayouts,
      onProjectChange,
    };
  },
});
